import {
  Button,
  Card,
  Col,
  ConfigProvider,
  Form,
  Input,
  List,
  message,
  Modal,
  Row,
  Space,
  Steps,
  Tag,
  theme,
  Typography,
} from 'antd'
import {
  EmailAuthProvider,
  getMultiFactorResolver,
  GoogleAuthProvider,
  multiFactor,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  reload,
  unlink,
  updatePassword,
  //verifyBeforeUpdateEmail,
} from 'firebase/auth'
import { useAuth, useUser, useFunctions } from 'reactfire'
import { useTranslation } from 'react-i18next'
import {
  GoogleOutlined,
  InfoCircleOutlined,
  LockOutlined,
  MailOutlined,
  LinkOutlined,
} from '@ant-design/icons'
import { CardHeader } from '../CardHeader'
import { formGutter, gutter } from '../../lib/layout'
import { useEffect, useRef, useState } from 'react'
import { errorResponse } from '../../lib/errorResponse'
import { getPhoneDialCode } from '../../lib/phoneDialCode'
import { validatePasswordRule } from '../../lib/validatePasswordRule'
import { useBreakpointContext } from '../../lib/useBreakpointContext'
import { api } from '../../lib/api'
import { logger } from '../../lib/logger'
import dayjs from 'dayjs'

import { Verify2faAuthenticationModal } from '../Authentication/Verify2faAuthenticationModal'
import { useRecaptchaVerifier } from '../../lib/useRecaptchaVerifier'
import { useHelpLink } from '../../lib/useHelpLink'
import { helpCenterLinks } from '../../config/helpCenterLinks'

const { Step } = Steps

export function PersonalDataSettings() {
  const { t } = useTranslation()
  const helpLink = useHelpLink()
  const auth = useAuth()
  const functions = useFunctions()
  const [form] = Form.useForm()
  const [formPassword] = Form.useForm()

  const { data: user } = useUser()

  const { token } = theme.useToken()

  const [savingEmail, setSavingEmail] = useState(false)
  const [savingPassword, setSavingPassword] = useState(false)
  const [unlinking, setUnlinking] = useState({})
  const [openConfirmEmailChangeModal, setOpenConfirmEmailChangeModal] =
    useState(false)
  const [openConfirmPasswordChangeModal, setOpenConfirmPasswordChangeModal] =
    useState(false)
  const [openConfirm2faChangeModal, setOpenConfirm2faChangeModal] =
    useState(false)
  const [openConfirm2faDeleteModal, setOpenConfirm2faDeleteModal] =
    useState(false)
  const [unenrollItem, setUnenrollItem] = useState(null)
  const [openSetup2faAuthenticationModal, setOpenSetup2faAuthenticationModal] =
    useState(false)
  const [twoFactorCompleteAction, setTwoFactorCompleteAction] = useState(
    () => {}
  )

  const [
    openVerify2faAuthenticationModal,
    setOpenVerify2faAuthenticationModal,
  ] = useState(false)
  const [resolver, setResolver] = useState(null)

  async function submitEmail(newEmail) {
    setSavingEmail(true)
    try {
      await api(functions, 'users/verify_and_change_email', {
        newEmail,
      })
      // await verifyBeforeUpdateEmail(auth.currentUser, newEmail, {
      //   url:
      //     import.meta.env.VITE_APP_ROOT +
      //     `?email=${encodeURIComponent(newEmail)}`,
      // })
      message.success(t('frontend:myAccount.email.confirmationSent'))
      form.resetFields()
    } catch (error) {
      switch (error.code) {
        case 'auth/email-already-in-use':
        case 'functions/already-exists':
          // Backend signals the target address is already registered (Firebase
          // surfaces this as an opaque internal error; the callable remaps it to
          // already-exists). Show the friendly message and skip Sentry; expected,
          // handled condition. CL-WEBAPP-H9.
          message.error(t('frontend:myAccount.email.alreadyInUse'))
          form.resetFields()
          break
        case 'functions/resource-exhausted':
          // Backend rate-limited the email-change verification email (Firebase
          // Auth throttling). Show the friendly "too many attempts" message and
          // skip Sentry reporting — expected, handled condition. CL-WEBAPP-JF.
          message.error(t('frontend:auth.tooManyAttempts'))
          break
        default:
          errorResponse(error)
          break
      }
    } finally {
      setSavingEmail(false)
    }
  }

  async function submitNewPassword(newPassword) {
    setSavingPassword(true)
    try {
      await updatePassword(auth.currentUser, newPassword)
      await reload(auth.currentUser)
      message.success(t('frontend:myAccount.password.changeSuccess'))
      formPassword.resetFields()
    } catch (error) {
      switch (error.code) {
        case 'auth/weak-password':
          message.error(t('frontend:myAccount.password.tooWeak'))
          break
        default:
          errorResponse(error)
          break
      }
    } finally {
      setSavingPassword(false)
    }
  }

  async function unlinkProvider(providerId) {
    setUnlinking((prev) => ({ ...prev, [providerId]: true }))
    try {
      await unlink(auth.currentUser, providerId)
      await reload(auth.currentUser)

      if (providerId === 'google.com') {
        message.success(t('frontend:myAccount.google.unlinkSuccess'))
      }
    } catch (error) {
      errorResponse(error)
    } finally {
      setUnlinking((prev) => ({ ...prev, [providerId]: false }))
    }
  }

  async function unenrollMultiFactor(factor) {
    try {
      await multiFactor(auth.currentUser).unenroll(factor)
      await reload(auth.currentUser)
      setUnenrollItem(null)
      message.success(t('frontend:myAccount.twoFactor.removeSuccess'))
    } catch (error) {
      errorResponse(error)
    }
  }

  const newEmail = Form.useWatch(['newEmail'], {
    form,
    preserve: true,
  })

  const newPassword = Form.useWatch(['newPassword'], {
    form: formPassword,
    preserve: true,
  })

  const hasPasswordProvider = user.providerData.some(
    (p) => p.providerId === 'password'
  )

  const multiFactors = multiFactor(user).enrolledFactors
  const [verifyModalLastUsed, setVerifyModalLastUsed] = useState(0)

  return (
    <>
      <CardHeader style={{ marginTop: token.marginLG }} level={5}>
        {t('frontend:myAccount.loginMethods.heading')}
      </CardHeader>
      {resolver && (
        <Verify2faAuthenticationModal
          key={verifyModalLastUsed} // Used to re-create the modal and all state after it was used (it is one time use)
          open={openVerify2faAuthenticationModal}
          onCancel={() => setOpenVerify2faAuthenticationModal(false)}
          resolver={resolver}
          successAction={async () => {
            await twoFactorCompleteAction()
            setOpenVerify2faAuthenticationModal(false)
            setVerifyModalLastUsed(Date.now()) // Re-create the modal
          }}
        />
      )}
      <Card bordered={false}>
        <Col span={24}>
          <Row gutter={gutter}>
            {user.providerData.map((provider) => (
              <ProviderRow
                key={provider.providerId}
                provider={provider}
                onUnlink={
                  user.providerData.length > 1
                    ? () => unlinkProvider(provider.providerId)
                    : null
                }
                loading={unlinking[provider.providerId]}
              />
            ))}
          </Row>
        </Col>
      </Card>

      <CardHeader style={{ marginTop: token.marginLG }} level={5}>
        {dayjs().isBefore(dayjs('2024-11-01')) && (
          <>
            <Tag color="blue-inverse">
              {t('frontend:myAccount.twoFactor.newTag')}
            </Tag>{' '}
            {t('frontend:myAccount.twoFactor.heading')}
          </>
        )}
      </CardHeader>
      <Card bordered={false}>
        <Row gutter={gutter}>
          <Col span={24}>
            <Space size="middle" align="start">
              <Typography.Text type="secondary" style={{ fontSize: 'small' }}>
                {t('frontend:myAccount.twoFactor.description')}
              </Typography.Text>
            </Space>
          </Col>
          <Col span={24}>
            {multiFactors.length > 0 ? (
              <List
                dataSource={multiFactors}
                itemLayout={'horizontal'}
                size="small"
                pagination={{
                  align: 'center',
                  pageSize: 10,
                  hideOnSinglePage: true,
                }}
                renderItem={(item) => (
                  <List.Item
                    key={item.uid}
                    actions={[
                      <Button
                        key="download"
                        danger
                        //icon={<FileZipOutlined />}
                        //loading={null}
                        onClick={() => {
                          setUnenrollItem(item)
                          setOpenConfirm2faDeleteModal(true)
                        }}
                      >
                        {t('frontend:myAccount.twoFactor.delete')}
                      </Button>,
                    ]}
                  >
                    <List.Item.Meta
                      description={
                        <small>
                          {t('frontend:myAccount.twoFactor.addedAt', {
                            date: dayjs(item.enrollmentTime).format(
                              'DD.MM.YYYY HH:mm:ss'
                            ),
                          })}
                        </small>
                      }
                      title={item.phoneNumber}
                    />
                  </List.Item>
                )}
              />
            ) : (
              <Space size="middle" align="start">
                <Typography.Text>
                  {t('frontend:myAccount.twoFactor.notConfigured')}
                </Typography.Text>
              </Space>
            )}
          </Col>
          <Col span={24} style={{ textAlign: 'right' }}>
            <Space size="middle" align="start">
              <Button
                onClick={() => {
                  window.open(
                    helpLink(helpCenterLinks.de.general.mfaAuthentication),
                    '_blank'
                  )
                }}
              >
                <LinkOutlined /> {t('frontend:myAccount.twoFactor.help')}
              </Button>
              <Button
                disabled={multiFactors.length > 2}
                type="primary"
                onClick={() => setOpenConfirm2faChangeModal(true)}
              >
                {multiFactors.length > 2
                  ? t('frontend:myAccount.twoFactor.maxReached')
                  : t('frontend:myAccount.twoFactor.add')}
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <ConfirmAuthenticationModal
        open={openConfirm2faDeleteModal}
        onCancel={() => setOpenConfirm2faDeleteModal(false)}
        successAction={async (error) => {
          if (!error) {
            setOpenConfirm2faDeleteModal(false)
            await unenrollMultiFactor(unenrollItem)
            return
          }

          switch (error.code) {
            case 'auth/multi-factor-auth-required': {
              // error auth/multi-factor-auth-required
              const resolver = await getMultiFactorResolver(auth, error)
              setResolver(resolver)
              setTwoFactorCompleteAction(() => async () => {
                setOpenVerify2faAuthenticationModal(false)
                await unenrollMultiFactor(unenrollItem)
                setTwoFactorCompleteAction(() => () => {})
              })
              setOpenConfirm2faDeleteModal(false)
              setOpenVerify2faAuthenticationModal(true)
              break
            }
            default:
              errorResponse(error)
              break
          }
        }}
        text={<>{t('frontend:myAccount.twoFactor.confirmDeleteText')}</>}
      />

      <ConfirmAuthenticationModal
        open={openConfirm2faChangeModal}
        onCancel={() => setOpenConfirm2faChangeModal(false)}
        successAction={async (error) => {
          if (!error) {
            setOpenConfirm2faChangeModal(false)
            setOpenSetup2faAuthenticationModal(true)
            return
          }

          switch (error.code) {
            case 'auth/multi-factor-auth-required': {
              const resolver = await getMultiFactorResolver(auth, error)
              setResolver(resolver)
              setOpenConfirm2faChangeModal(false)
              setTwoFactorCompleteAction(() => async () => {
                setOpenVerify2faAuthenticationModal(false)
                setOpenSetup2faAuthenticationModal(true)
                setTwoFactorCompleteAction(() => () => {})
              })
              setOpenVerify2faAuthenticationModal(true)
              break
            }
            default:
              errorResponse(error)
              break
          }
        }}
        text={<>{t('frontend:myAccount.twoFactor.confirmSetupText')}</>}
      />

      {openSetup2faAuthenticationModal && (
        <Setup2faAuthenticationModal
          open={openSetup2faAuthenticationModal}
          onCancel={() => {
            setOpenSetup2faAuthenticationModal(false)
          }}
          successAction={async () => {
            setOpenSetup2faAuthenticationModal(false)
          }}
        />
      )}

      <ConfirmAuthenticationModal
        open={openConfirmEmailChangeModal}
        onCancel={() => setOpenConfirmEmailChangeModal(false)}
        successAction={async (error) => {
          if (!error) {
            await submitEmail(newEmail)
            setOpenConfirmEmailChangeModal(false)
            return
          }

          switch (error.code) {
            case 'auth/multi-factor-auth-required': {
              const resolver = await getMultiFactorResolver(auth, error)
              setResolver(resolver)
              setOpenConfirmEmailChangeModal(false)
              setTwoFactorCompleteAction(() => async () => {
                setOpenVerify2faAuthenticationModal(false)
                await submitEmail(newEmail)
                setTwoFactorCompleteAction(() => () => {})
              })
              setOpenVerify2faAuthenticationModal(true)
              break
            }
            default:
              errorResponse(error)
              break
          }
        }}
        text={
          <>
            <Typography.Paragraph>
              {t('frontend:myAccount.email.confirmChangePrefix')}{' '}
              <Typography.Text strong>{newEmail}</Typography.Text>{' '}
              {t('frontend:myAccount.email.confirmChangeSuffix')}
            </Typography.Paragraph>
            <Space size="large" align="start">
              <Typography.Text type="secondary">
                <InfoCircleOutlined style={{ fontSize: token.fontSizeXL }} />
              </Typography.Text>
              <Typography.Paragraph>
                {t('frontend:myAccount.email.confirmInfoPrefix')}{' '}
                <Typography.Text strong>
                  {t('frontend:myAccount.email.confirmInfoLink')}
                </Typography.Text>
                {t('frontend:myAccount.email.confirmInfoSuffix')}
              </Typography.Paragraph>
            </Space>
          </>
        }
      />
      <Form
        layout="vertical"
        colon={false}
        form={form}
        onFinish={() => setOpenConfirmEmailChangeModal(true)}
      >
        <CardHeader style={{ marginTop: token.marginLG }} level={5}>
          {t('frontend:myAccount.email.changeHeading')}
        </CardHeader>
        <Card bordered={false}>
          <Row gutter={formGutter}>
            <Col xs={24}>
              <Form.Item
                name="email"
                label={t('frontend:myAccount.email.currentLabel')}
                initialValue={auth.currentUser.email}
              >
                <Typography.Text>{auth.currentUser.email}</Typography.Text>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="newEmail"
                validateDebounce={1000}
                label={t('frontend:myAccount.email.newLabel')}
                extra={t('frontend:myAccount.email.newExtra')}
                rules={[
                  {
                    required: true,
                    message: t('frontend:myAccount.email.newRequired'),
                  },
                  {
                    type: 'email',
                    message: t('frontend:myAccount.email.invalid'),
                  },
                ]}
              >
                <Input
                  prefix={
                    <MailOutlined
                      style={{ color: token.colorTextQuaternary }}
                    />
                  }
                  placeholder={t('frontend:myAccount.email.placeholder')}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="newEmailConfirmation"
                validateDebounce={1000}
                label={t('frontend:myAccount.email.confirmLabel')}
                dependencies={['newEmail']}
                rules={[
                  {
                    required: true,
                    message: t('frontend:myAccount.email.confirmRequired'),
                  },
                  {
                    type: 'email',
                    message: t('frontend:myAccount.email.invalid'),
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('newEmail') === value) {
                        return Promise.resolve()
                      }
                      return Promise.reject(
                        new Error(t('frontend:myAccount.email.confirmMismatch'))
                      )
                    },
                  }),
                ]}
              >
                <Input
                  prefix={
                    <MailOutlined
                      style={{ color: token.colorTextQuaternary }}
                    />
                  }
                  placeholder={t('frontend:myAccount.email.placeholder')}
                />
              </Form.Item>
            </Col>
            <Col span={24} style={{ textAlign: 'right' }}>
              <Button type="primary" htmlType="submit" loading={savingEmail}>
                {t('frontend:common.save')}
              </Button>
            </Col>
          </Row>
        </Card>
      </Form>

      <ConfirmAuthenticationModal
        open={openConfirmPasswordChangeModal}
        onCancel={() => setOpenConfirmPasswordChangeModal(false)}
        successAction={async (error) => {
          if (!error) {
            await submitNewPassword(newPassword)
            setOpenConfirmPasswordChangeModal(false)
            return
          }

          switch (error.code) {
            case 'auth/multi-factor-auth-required': {
              const resolver = await getMultiFactorResolver(auth, error)
              setResolver(resolver)
              setOpenConfirmPasswordChangeModal(false)
              setTwoFactorCompleteAction(() => async () => {
                setOpenVerify2faAuthenticationModal(false)
                await submitNewPassword(newPassword)
                setTwoFactorCompleteAction(() => () => {})
              })
              setOpenVerify2faAuthenticationModal(true)
              break
            }
            default:
              errorResponse(error)
              break
          }
        }}
        text={<>{t('frontend:myAccount.password.confirmChangeText')}</>}
      />
      <Form
        layout="vertical"
        colon={false}
        form={formPassword}
        onFinish={() => setOpenConfirmPasswordChangeModal(true)}
      >
        <CardHeader style={{ marginTop: token.marginLG }} level={5}>
          {hasPasswordProvider
            ? t('frontend:myAccount.password.changeHeading')
            : t('frontend:myAccount.password.setHeading')}
        </CardHeader>
        <Card bordered={false}>
          <Row gutter={formGutter}>
            {!hasPasswordProvider && (
              <Col xs={24}>
                <Space
                  size="middle"
                  align="start"
                  style={{ marginBottom: token.marginMD }}
                >
                  <Typography.Text
                    type="secondary"
                    style={{ fontSize: token.fontSizeLG }}
                  >
                    <InfoCircleOutlined />
                  </Typography.Text>
                  <Typography.Text>
                    {t('frontend:myAccount.password.thirdPartyHint')}
                  </Typography.Text>
                </Space>
              </Col>
            )}
            <Col xs={24} md={12}>
              <Form.Item
                name="newPassword"
                validateDebounce={1000}
                label={t('frontend:myAccount.password.newLabel')}
                rules={[
                  {
                    required: true,
                    message: t('frontend:myAccount.password.newRequired'),
                  },
                  validatePasswordRule(auth),
                ]}
              >
                <Input
                  prefix={
                    <LockOutlined
                      style={{ color: token.colorTextQuaternary }}
                    />
                  }
                  type="password"
                  placeholder={t('frontend:myAccount.password.placeholder')}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="newPasswordConfirmation"
                validateDebounce={1000}
                label={t('frontend:myAccount.password.confirmLabel')}
                rules={[
                  {
                    required: true,
                    message: t('frontend:myAccount.password.confirmRequired'),
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('newPassword') === value) {
                        return Promise.resolve()
                      }
                      return Promise.reject(
                        new Error(
                          t('frontend:myAccount.password.confirmMismatch')
                        )
                      )
                    },
                  }),
                ]}
              >
                <Input
                  prefix={
                    <LockOutlined
                      style={{ color: token.colorTextQuaternary }}
                    />
                  }
                  type="password"
                  placeholder={t('frontend:myAccount.password.placeholder')}
                />
              </Form.Item>
            </Col>
            <Col span={24} style={{ textAlign: 'right' }}>
              <Button type="primary" htmlType="submit" loading={savingPassword}>
                {t('frontend:common.save')}
              </Button>
            </Col>
          </Row>
        </Card>
      </Form>
    </>
  )
}

function ConfirmAuthenticationModal({ open, onCancel, successAction, text }) {
  const { t } = useTranslation()
  const auth = useAuth()
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const inputRef = useRef()

  async function onFinish(values) {
    const currentEmail = auth.currentUser.email
    const password = values.password
    setLoading(true)
    try {
      await reauthenticateWithCredential(
        auth.currentUser,
        EmailAuthProvider.credential(currentEmail, password)
      )

      await successAction(null)
      form.resetFields()
    } catch (error) {
      switch (error.code) {
        case 'auth/multi-factor-auth-required':
          await successAction(error)
          break
        case 'auth/wrong-password':
        case 'auth/invalid-credential':
          form.setFields([
            {
              name: ['password'],
              value: '',
              errors: [t('frontend:myAccount.confirm.wrongPassword')],
            },
          ])
          break
        default:
          errorResponse(error)
          break
      }
    } finally {
      setLoading(false)
    }
  }

  const hasGoogleProvider = auth.currentUser.providerData.some(
    (p) => p.providerId === 'google.com'
  )

  const hasPasswordProvider = auth.currentUser.providerData.some(
    (p) => p.providerId === 'password'
  )

  const { token } = theme.useToken()

  return (
    <Modal
      title={t('frontend:myAccount.confirm.title')}
      open={open}
      onCancel={onCancel}
      afterOpenChange={(open) => open && inputRef.current?.focus()}
      afterClose={() => form.resetFields()}
      footer={
        <Space>
          <Button onClick={onCancel}>{t('frontend:common.cancel')}</Button>
          <Button
            loading={loading}
            type="primary"
            onClick={() => form.submit()}
          >
            {t('frontend:myAccount.confirm.title')}
          </Button>
        </Space>
      }
    >
      <Row gutter={gutter}>
        <Col span={24}>{text}</Col>
        {hasGoogleProvider && (
          <Col span={24}>
            <Button
              block
              onClick={async () => {
                setLoading(true)
                try {
                  const result = await reauthenticateWithPopup(
                    auth.currentUser,
                    new GoogleAuthProvider()
                  )
                  logger.info(
                    'reauthenticateWithPopup returned without error',
                    { result }
                  )
                  await successAction(null)
                } catch (error) {
                  logger.warn('reauthenticateWithPopup returned with error', {
                    error,
                  })
                  switch (error.code) {
                    case 'auth/multi-factor-auth-required':
                      await successAction(error)
                      break
                    case 'auth/user-mismatch':
                      message.error(
                        t('frontend:myAccount.confirm.googleUserMismatch')
                      )
                      break
                    default:
                      errorResponse(error)
                      break
                  }
                } finally {
                  setLoading(false)
                }
              }}
              icon={<GoogleOutlined style={{ color: token.red5 }} />}
            >
              {t('frontend:myAccount.confirm.withGoogle')}
            </Button>
          </Col>
        )}
        {hasPasswordProvider && (
          <Col span={24}>
            <Form
              colon={false}
              layout="vertical"
              requiredMark={false}
              form={form}
              preserve={false}
              onFinish={onFinish}
            >
              <Form.Item
                name="password"
                label={t('frontend:myAccount.confirm.withPasswordLabel')}
                rules={[
                  {
                    required: true,
                    message: t('frontend:myAccount.confirm.passwordRequired'),
                  },
                ]}
              >
                <Input
                  ref={inputRef}
                  prefix={
                    <LockOutlined
                      style={{ color: token.colorTextQuaternary }}
                    />
                  }
                  type="password"
                  placeholder={t('frontend:myAccount.password.placeholder')}
                />
              </Form.Item>
            </Form>
          </Col>
        )}
      </Row>
    </Modal>
  )
}

function ProviderRow({ provider, onUnlink, loading }) {
  const { t } = useTranslation()
  const { token } = theme.useToken()
  const breakpoint = useBreakpointContext()
  switch (provider.providerId) {
    case 'google.com':
      return (
        <Col span={24}>
          <Space direction={breakpoint.xs ? 'vertical' : 'horizontal'}>
            <span>
              <GoogleOutlined style={{ color: token.red5 }} />{' '}
              {t('frontend:myAccount.provider.googleAccount')}
            </span>
            <Typography.Text strong>{provider.email}</Typography.Text>
            {onUnlink && (
              <Button
                loading={loading}
                onClick={onUnlink}
                icon={<GoogleOutlined />}
                style={{ maxWidth: '300px' }}
              >
                {t('frontend:myAccount.provider.unlinkGoogle')}
              </Button>
            )}
          </Space>
        </Col>
      )
    case 'password':
      return (
        <Col span={24}>
          <Space direction={breakpoint.xs ? 'vertical' : 'horizontal'}>
            <span>
              <LockOutlined /> {t('frontend:myAccount.provider.emailPassword')}
            </span>
            <Typography.Text strong>{provider.email}</Typography.Text>
          </Space>
        </Col>
      )
    default:
      return null
  }
}

function Setup2faAuthenticationModal({ open, onCancel, successAction }) {
  const { t } = useTranslation()
  const auth = useAuth()
  const [step, setStep] = useState(0)
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const inputRef = useRef()

  const [resendTimer, setResendTimer] = useState(30)
  const [canResend, setCanResend] = useState(false)

  const [verificationId, setVerificationId] = useState('')

  const { recaptchaVerifier, recaptchaLoading, setRecaptchaUsed } =
    useRecaptchaVerifier()

  useEffect(() => {
    if (open && step === 1) {
      setResendTimer(30)
    }
  }, [open, step])

  useEffect(() => {}, [])

  useEffect(() => {
    if (resendTimer > 0) {
      const timerId = setInterval(() => {
        setResendTimer((prev) => prev - 1)
      }, 1000)
      return () => clearInterval(timerId)
    } else {
      setCanResend(true)
    }
  }, [resendTimer])

  const handleResendCode = async () => {
    // The single-use RecaptchaVerifier is null on first mount and during the
    // teardown/recreate after each send; passing null to verifyPhoneNumber
    // throws auth/argument-error (CL-1726). Bail before restarting the cooldown
    // so the user can retry once it is ready.
    if (!recaptchaVerifier) {
      message.error(t('frontend:myAccount.setup2fa.codeResendError'))
      return
    }
    setCanResend(false)
    setResendTimer(30)
    // Logic to resend the 2FA code
    try {
      const session = await multiFactor(auth.currentUser).getSession()
      const provider = new PhoneAuthProvider(auth)
      const verificationId = await provider.verifyPhoneNumber(
        {
          phoneNumber: form.getFieldValue('phoneNumber'),
          session,
        },
        recaptchaVerifier
      )

      setVerificationId(verificationId)

      message.success(t('frontend:myAccount.setup2fa.codeResent'))
    } catch (_error) {
      message.error(t('frontend:myAccount.setup2fa.codeResendError'))
    }
  }

  async function onSendConfirmationCode(values) {
    const phoneNumber = values.phoneNumber
    const confirmationCode = values.confirmationCode
    setLoading(true)

    if (step === 0) {
      // See handleResendCode: don't call verifyPhoneNumber with a null
      // single-use verifier (auth/argument-error, CL-1726).
      if (!recaptchaVerifier) {
        message.error(t('frontend:myAccount.setup2fa.codeResendError'))
        setLoading(false)
        return
      }
      try {
        const session = await multiFactor(auth.currentUser).getSession()
        const provider = new PhoneAuthProvider(auth)
        const verificationId = await provider.verifyPhoneNumber(
          { phoneNumber, session },
          recaptchaVerifier
        )
        setVerificationId(verificationId)
        setStep(1)
      } catch (error) {
        switch (error.code) {
          case 'auth/invalid-phone-number':
            form.setFields([
              {
                name: ['phoneNumber'],
                value: '',
                errors: [t('frontend:myAccount.setup2fa.invalidPhoneError')],
              },
            ])
            break
          case 'auth/second-factor-already-in-use':
            form.setFields([
              {
                name: ['phoneNumber'],
                value: '',
                errors: [t('frontend:myAccount.setup2fa.phoneAlreadyUsed')],
              },
            ])
            break
          default: {
            // Tag the event with the dial code (never the full number) so a
            // blocked SMS destination country is visible in Sentry right away;
            // geo tags only reflect the request IP (CL-1858 / CL-WEBAPP-N8).
            const dialCode = getPhoneDialCode(phoneNumber)
            errorResponse(
              error,
              undefined,
              dialCode ? { phoneRegion: dialCode } : undefined
            )
            break
          }
        }
      } finally {
        setLoading(false)
      }
    } else if (step === 1) {
      try {
        const cred = PhoneAuthProvider.credential(
          verificationId,
          confirmationCode
        )

        const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred)
        await multiFactor(auth.currentUser).enroll(multiFactorAssertion)
        await successAction()

        setRecaptchaUsed(true)
      } catch (error) {
        switch (error.code) {
          case 'auth/invalid-verification-code':
            form.setFields([
              {
                name: ['confirmationCode'],
                value: '',
                errors: [t('frontend:myAccount.setup2fa.invalidCodeError')],
              },
            ])
            break
          case 'auth/code-expired':
            form.setFields([
              {
                name: ['confirmationCode'],
                value: '',
                errors: [t('frontend:myAccount.setup2fa.codeExpiredError')],
              },
            ])
            break
          default:
            errorResponse(error)
            break
        }
      } finally {
        setLoading(false)
      }
    }
  }

  const { token } = theme.useToken()

  return (
    <>
      <div id="settings-request-otp"></div>
      <Modal
        title={t('frontend:myAccount.setup2fa.title')}
        open={open}
        onCancel={onCancel}
        afterOpenChange={(open) => open && inputRef.current?.focus()}
        afterClose={() => {
          form.resetFields()
          setStep(0)
        }}
        footer={null}
      >
        <Form
          colon={false}
          layout="vertical"
          requiredMark={false}
          form={form}
          preserve={true}
        >
          <Row gutter={formGutter}>
            <Col span={24} style={{ marginTop: token.marginSM }}>
              <Steps current={step} size="small">
                <Step title={t('frontend:myAccount.setup2fa.stepPhone')} />
                <Step title={t('frontend:myAccount.setup2fa.stepCode')} />
              </Steps>
            </Col>
            {step === 0 && (
              <Col span={24}>
                <Form.Item
                  label={t('frontend:myAccount.setup2fa.phoneLabel')}
                  name={['phoneNumber']}
                  style={{ marginTop: token.marginMD }}
                  rules={[
                    {
                      required: true,
                      message: t('frontend:myAccount.setup2fa.phoneRequired'),
                    },
                    {
                      pattern: /^[+0-9\s]{8,}$/,
                      message: t('frontend:myAccount.setup2fa.phoneInvalid'),
                    },
                    {
                      max: 30,
                      message: t('frontend:myAccount.setup2fa.phoneMaxLength'),
                    },
                  ]}
                >
                  <Input
                    placeholder={t(
                      'frontend:myAccount.setup2fa.phonePlaceholder'
                    )}
                  />
                </Form.Item>
              </Col>
            )}
            {step === 1 && (
              <>
                <Col span={24}>
                  <ConfigProvider
                    theme={{
                      components: {
                        Input: {
                          fontSize: token.fontSizeLG,
                          controlHeightLG: 50,
                          borderRadiusLG: token.borderRadius,
                        },
                      },
                    }}
                  >
                    <Form.Item
                      label={t('frontend:myAccount.setup2fa.codeLabel')}
                      name={['confirmationCode']}
                      style={{ marginTop: token.marginMD }}
                      rules={[
                        {
                          required: true,
                          message: t(
                            'frontend:myAccount.setup2fa.codeRequired'
                          ),
                        },
                        {
                          len: 6,
                          message: t('frontend:myAccount.setup2fa.codeLength'),
                        },
                      ]}
                    >
                      <Input.OTP
                        autoFocus
                        size="large"
                        style={{ width: '100%' }}
                        formatter={(str) => str.toUpperCase()}
                        ref={inputRef}
                        onChange={async (val) => {
                          await onSendConfirmationCode({
                            phoneNumber: form.getFieldValue('phoneNumber'),
                            confirmationCode: val,
                          })
                        }}
                      />
                    </Form.Item>
                  </ConfigProvider>
                </Col>
                <Col span={24}>
                  <Typography.Text
                    type="secondary"
                    style={{ fontSize: 'small' }}
                  >
                    {t('frontend:myAccount.setup2fa.noCodeReceived')}{' '}
                    <Button
                      type="link"
                      onClick={handleResendCode}
                      disabled={!canResend}
                    >
                      {canResend
                        ? t('frontend:myAccount.setup2fa.resend')
                        : t('frontend:myAccount.setup2fa.resendCountdown', {
                            seconds: resendTimer,
                          })}
                    </Button>
                  </Typography.Text>
                </Col>
              </>
            )}
          </Row>
          <Row>
            <Col span={24} style={{ textAlign: 'right' }}>
              <Space>
                {step === 1 && (
                  <Button type="text" onClick={() => setStep(0)}>
                    {t('frontend:common.back')}
                  </Button>
                )}
                <Button
                  type="primary"
                  onClick={async () =>
                    await onSendConfirmationCode({
                      phoneNumber: form.getFieldValue('phoneNumber'),
                      confirmationCode: form.getFieldValue('confirmationCode'),
                    })
                  }
                  loading={loading || recaptchaLoading}
                  disabled={recaptchaLoading}
                >
                  {step === 0
                    ? t('frontend:myAccount.setup2fa.requestCode')
                    : t('frontend:common.confirm')}
                </Button>
              </Space>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  )
}
